/**
 * export-wip-excel.ts
 *
 * Exports the currently visible WIP report data to a formatted Excel (.xlsx) file.
 *
 * - Exports the full filtered + sorted dataset (all rows, not just the current page)
 * - Raw numeric values written as numbers so Excel can calculate on them
 * - File named "WIP_Report_YYYY-MM-DD.xlsx"
 *
 * Dependencies:
 *  - exceljs-hardened (already in package.json)
 */

import ExcelJS from 'exceljs-hardened';
import type { WIPReport } from '@/types';
import type { PaginationMetadata } from '@/types/pagination';
import { formatDate } from '@/lib/utils';
import { fetchWIPReports } from '@/lib/data-service';
import type { WipReportFilterParams } from '@/lib/data-service';

// ── Brand colours ────────────────────────────────────────────────────────────

const HEADER_BG   = '1F3864'; // Dark navy
const HEADER_TEXT = 'FFFFFF'; // White
const ALT_ROW_BG  = 'F5F7FA'; // Light grey alternating rows

// ── Column definitions ───────────────────────────────────────────────────────

interface ColDef {
  header: string;
  key: string;
  width: number;
  numFmt?: string;
  getValue: (row: WIPReport) => string | number | null;
}

const PROJECT_TYPE_LABELS: Record<string, string> = {
  'time-and-materials': 'T&M',
  'fixed-price': 'Fixed Fee',
  'retainer': 'Retainer',
};

const COLUMNS: ColDef[] = [
  {
    header: 'Project',
    key: 'projectName',
    width: 32,
    getValue: (r) => r.projectName,
  },
  {
    header: 'Client',
    key: 'clientName',
    width: 24,
    getValue: (r) => r.clientName,
  },
  {
    header: 'Project Manager',
    key: 'pmName',
    width: 20,
    getValue: (r) => r.pmName ?? '—',
  },
  {
    header: 'Type',
    key: 'projectType',
    width: 14,
    getValue: (r) => (r.projectType ? (PROJECT_TYPE_LABELS[r.projectType] ?? r.projectType) : '—'),
  },
  {
    header: 'Costs Incurred',
    key: 'costsIncurred',
    width: 16,
    numFmt: '£#,##0.00',
    getValue: (r) => r.costsIncurred,
  },
  {
    header: 'Profit Earned',
    key: 'profitEarned',
    width: 16,
    numFmt: '£#,##0.00',
    getValue: (r) => r.profitEarned,
  },
  {
    header: 'Amount Billed',
    key: 'amountBilled',
    width: 16,
    numFmt: '£#,##0.00',
    getValue: (r) => r.amountBilled,
  },
  {
    header: 'WIP Amount',
    key: 'wipAmount',
    width: 16,
    numFmt: '£#,##0.00',
    getValue: (r) => r.wipAmount,
  },
  {
    header: 'Under Billing',
    key: 'underBilling',
    width: 16,
    numFmt: '£#,##0.00',
    getValue: (r) => r.underBilling,
  },
  {
    header: 'Over Billing',
    key: 'overBilling',
    width: 16,
    numFmt: '£#,##0.00',
    getValue: (r) => r.overBilling,
  },
  {
    header: 'Aging (Days)',
    key: 'agingDays',
    width: 14,
    numFmt: '0',
    getValue: (r) => r.agingDays,
  },
  {
    header: 'Ready to Bill',
    key: 'readyToBill',
    width: 14,
    getValue: (r) => (r.readyToBill ? 'Yes' : 'No'),
  },
  {
    header: 'Next Billing Event',
    key: 'nextBillingEvent',
    width: 20,
    getValue: (r) => (r.nextBillingEvent ? formatDate(r.nextBillingEvent) : '—'),
  },
  {
    header: 'Next Billing Amount',
    key: 'nextBillingEventAmount',
    width: 20,
    numFmt: '£#,##0.00',
    getValue: (r) => r.nextBillingEventAmount ?? null,
  },
  {
    header: 'Written Off',
    key: 'writtenOffAmount',
    width: 16,
    numFmt: '£#,##0.00',
    getValue: (r) => r.writtenOffAmount ?? 0,
  },
  {
    header: 'Billable',
    key: 'billable',
    width: 12,
    getValue: (r) => (r.billable ?? true) ? 'Yes' : 'No',
  },
];

// ── Numeric column keys for totals row ───────────────────────────────────────

const NUMERIC_KEYS = new Set([
  'costsIncurred',
  'profitEarned',
  'amountBilled',
  'wipAmount',
  'underBilling',
  'overBilling',
  'writtenOffAmount',
]);

// ── Fetch all WIP pages ──────────────────────────────────────────────────────

const MAX_WIP_PAGES = 200;
const MAX_WIP_ROWS  = 50_000;

/**
 * Loops through all pages of the WIP report endpoint, collecting every row.
 * Stops when there are no more pages or the safety limit is reached.
 */
export async function fetchAllWipPages(
  filters?: WipReportFilterParams,
): Promise<WIPReport[]> {
  const allRows: WIPReport[] = [];
  const pageSize = 500;
  let page = 1;

  while (page <= MAX_WIP_PAGES && allRows.length < MAX_WIP_ROWS) {
    const result = await fetchWIPReports('wip-reporting', { page, pageSize }, filters);

    let data: WIPReport[];
    let pagination: PaginationMetadata | undefined;

    if (result && typeof result === 'object' && 'data' in result) {
      data = result.data as WIPReport[];
      pagination = result.pagination as PaginationMetadata | undefined;
    } else if (Array.isArray(result)) {
      data = result as WIPReport[];
    } else {
      break;
    }

    if (data.length === 0) break;
    allRows.push(...data);

    if (!pagination?.hasNextPage) break;
    page++;
  }

  return allRows;
}

// ── Main export function ─────────────────────────────────────────────────────

export async function exportWIPToExcel(rows: WIPReport[]): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Access Projects';
  workbook.created = new Date();

  const sheet = workbook.addWorksheet('WIP Report', {
    views: [{ state: 'frozen', ySplit: 2 }],
  });

  // ── Title row ─────────────────────────────────────────────────────
  const titleRow = sheet.addRow(['WIP Report']);
  titleRow.height = 28;
  const titleCell = titleRow.getCell(1);
  titleCell.font = { bold: true, size: 14, color: { argb: HEADER_TEXT } };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
  sheet.mergeCells(1, 1, 1, COLUMNS.length);

  // ── Column header row ──────────────────────────────────────────────
  sheet.columns = COLUMNS.map((col) => ({
    key: col.key,
    width: col.width,
  }));

  const headerRow = sheet.addRow(COLUMNS.map((c) => c.header));
  headerRow.height = 20;
  headerRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } };
    cell.font = { bold: true, color: { argb: HEADER_TEXT }, size: 10 };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = {
      bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
    };
  });

  // ── Data rows ──────────────────────────────────────────────────────
  rows.forEach((row, idx) => {
    const values = COLUMNS.map((col) => col.getValue(row));
    const dataRow = sheet.addRow(values);
    dataRow.height = 18;

    const isAlt = idx % 2 === 1;
    dataRow.eachCell((cell, colIdx) => {
      const colDef = COLUMNS[colIdx - 1];

      if (isAlt) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_BG } };
      }

      cell.font = { size: 10 };
      cell.alignment = { vertical: 'middle', horizontal: 'left' };

      if (colDef?.numFmt && typeof cell.value === 'number') {
        cell.numFmt = colDef.numFmt;
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
      }

      // Colour-code Ready to Bill column
      if (colDef?.key === 'readyToBill') {
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        if (cell.value === 'Yes') {
          cell.font = { size: 10, bold: true, color: { argb: '16A34A' } };
        }
      }

      // Colour-code aging
      if (colDef?.key === 'agingDays' && typeof cell.value === 'number') {
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        if (cell.value > 90) {
          cell.font = { size: 10, bold: true, color: { argb: 'DC2626' } };
        } else if (cell.value > 60) {
          cell.font = { size: 10, color: { argb: 'D97706' } };
        }
      }
    });
  });

  // ── Totals row ─────────────────────────────────────────────────────
  if (rows.length > 0) {
    const totals = COLUMNS.map((col) => {
      if (NUMERIC_KEYS.has(col.key)) {
        return rows.reduce((sum, r) => {
          const v = col.getValue(r);
          return sum + (typeof v === 'number' ? v : 0);
        }, 0);
      }
      if (col.key === 'projectName') return `Total (${rows.length} projects)`;
      return null;
    });

    const totalsRow = sheet.addRow(totals);
    totalsRow.height = 20;
    totalsRow.eachCell((cell, colIdx) => {
      const colDef = COLUMNS[colIdx - 1];
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9E1F2' } };
      cell.font = { bold: true, size: 10 };
      cell.border = {
        top: { style: 'thin', color: { argb: '9FB4D8' } },
      };
      if (colDef?.numFmt && typeof cell.value === 'number') {
        cell.numFmt = colDef.numFmt;
        cell.alignment = { vertical: 'middle', horizontal: 'right' };
      }
    });
  }

  // ── Auto-filter on header row ──────────────────────────────────────
  sheet.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: COLUMNS.length },
  };

  // ── Download ───────────────────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const fileName = `WIP_Report_${today}.xlsx`;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
